import { useMemo, useState } from "react";
import {
  Calculator,
  CalendarClock,
  CreditCard,
  ReceiptText,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import type { Instalments } from "@ir/types";
import {
  calculateCustomerPricing,
  formatCurrency,
  formatPercent,
} from "@ir/services";
import { Badge, Card, CardHeader, DetailList, Field, Input, PageHeader, Select } from "@ir/ui";

const PROVIDER_OPTIONS = [
  { value: "granite", label: "Granite Underwriting" },
  { value: "inshur", label: "INSHUR UK Ltd" },
  { value: "kinetiq", label: "Kinetiq Underwriting" },
];

const PAYMENT_OPTIONS = [
  { value: "full", label: "Paid in Full" },
  { value: "10", label: "10 Monthly Payments" },
  { value: "11", label: "11 Monthly Payments" },
];

export function PricingCalculatorPage() {
  const [provider, setProvider] = useState("granite");
  const [premium, setPremium] = useState("1000");
  const [paymentScheme, setPaymentScheme] = useState("full");

  const parsedPremium = Number(premium);
  const normalizedPremium = Number.isFinite(parsedPremium) ? parsedPremium : 0;
  const instalments = paymentScheme === "10" || paymentScheme === "11"
    ? (Number(paymentScheme) as Instalments)
    : null;
  const pricing = useMemo(
    () => calculateCustomerPricing(provider, normalizedPremium, instalments),
    [instalments, normalizedPremium, provider],
  );
  const annualCheck = useMemo(
    () => calculateCustomerPricing(provider, normalizedPremium),
    [normalizedPremium, provider],
  );
  const tenMonthCheck = useMemo(
    () => calculateCustomerPricing(provider, normalizedPremium, 10),
    [normalizedPremium, provider],
  );
  const elevenMonthCheck = useMemo(
    () => calculateCustomerPricing(provider, normalizedPremium, 11),
    [normalizedPremium, provider],
  );

  return (
    <>
      <PageHeader
        icon={Calculator}
        title="Pricing calculator"
        description="Validate the fast-food marketplace customer price against the sales calculator."
      />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <Card>
          <CardHeader
            icon={ReceiptText}
            title="Quote inputs"
            description="Use the insurer premium returned by Granite, INSHUR, or Kinetiq."
          />
          <div className="grid gap-4">
            <Field label="Insurer">
              <Select
                value={provider}
                onChange={(event) => setProvider(event.target.value)}
                options={PROVIDER_OPTIONS}
              />
            </Field>
            <Field
              label="Insurer premium"
              hint="This is the raw provider premium before marketplace fees."
            >
              <Input
                type="number"
                min="0"
                step="0.01"
                value={premium}
                onChange={(event) => setPremium(event.target.value)}
                onWheel={(event) => event.currentTarget.blur()}
              />
            </Field>
            <Field label="Payment scheme">
              <Select
                value={paymentScheme}
                onChange={(event) => setPaymentScheme(event.target.value)}
                options={PAYMENT_OPTIONS}
              />
            </Field>
          </div>
        </Card>

        <Card>
          <div className="rounded-2xl bg-[radial-gradient(circle_at_top_left,rgba(212,162,64,0.24),transparent_34%),linear-gradient(135deg,#151412,#2d2618)] p-5 text-white shadow-ir-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <Badge tone="gold" icon={ShieldCheck}>Customer display</Badge>
                <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight">
                  {formatCurrency(pricing.totalSellingPrice)}
                </h2>
                <p className="mt-1 text-sm text-white/70">
                  Total selling price used on the public quote screen and quote email.
                </p>
              </div>
              <div className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-right">
                <p className="text-xs uppercase tracking-[0.12em] text-white/55">Provider premium</p>
                <p className="mt-1 text-xl font-semibold tabular">{formatCurrency(pricing.insurerPremium)}</p>
              </div>
            </div>
            {pricing.finance && (
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <HighlightTile label="Deposit" value={formatCurrency(pricing.finance.initialPayment)} />
                <HighlightTile
                  label={`${pricing.finance.instalments} monthly payments`}
                  value={formatCurrency(pricing.finance.monthlyPayment)}
                />
                <HighlightTile label="Total payable" value={formatCurrency(pricing.finance.totalPayable)} />
              </div>
            )}
          </div>

          <div className="mt-5 grid gap-4 2xl:grid-cols-2">
            <div className="min-w-0 rounded-xl border border-line bg-surface-sunken p-4">
              <h3 className="mb-3 text-sm font-semibold text-content">Price build-up</h3>
              <CalculatorRows
                items={[
                  { label: "Premium", value: formatCurrency(pricing.insurerPremium) },
                  { label: "Motor Legal", value: formatCurrency(pricing.motorLegalFee) },
                  { label: "Arrangement Fee", value: formatCurrency(pricing.arrangementFee) },
                  {
                    label: `Fee in Lieu (${formatPercent(pricing.feeInLieuRate)})`,
                    value: formatCurrency(pricing.feeInLieuAmount),
                  },
                ]}
              />
            </div>
            <div className="min-w-0 rounded-xl border border-line bg-surface-sunken p-4">
              <h3 className="mb-3 text-sm font-semibold text-content">Internal income check</h3>
              <CalculatorRows
                items={[
                  {
                    label: "Displayed standard commission",
                    value: formatPercent(pricing.displayedStandardCommissionRate),
                  },
                  {
                    label: "Insurer fee before commission",
                    value: formatCurrency(pricing.insurerFeeBeforeCommission),
                    hide: pricing.insurerFeeBeforeCommission <= 0,
                  },
                  { label: "Premium Comm", value: formatCurrency(pricing.premiumCommission) },
                  { label: "Fees income", value: formatCurrency(pricing.feesIncome) },
                  {
                    label: "Finance commission",
                    value: formatCurrency(pricing.financeCommission),
                    hide: !pricing.finance,
                  },
                  { label: "Total sales income", value: formatCurrency(pricing.totalSalesIncome) },
                ]}
              />
            </div>
          </div>

          {pricing.finance && (
            <div className="mt-5 rounded-lg border border-line bg-surface-sunken p-4">
              <h3 className="mb-3 text-sm font-semibold text-content">Finance details</h3>
              <DetailList
                items={[
                  { label: "Loan amount", value: formatCurrency(pricing.finance.loanAmount) },
                  {
                    label: `Initial payment (${formatPercent(pricing.finance.depositRate)})`,
                    value: formatCurrency(pricing.finance.initialPayment),
                  },
                  {
                    label: `Transaction fee (${formatPercent(pricing.finance.transactionFeeRate)})`,
                    value: formatCurrency(pricing.finance.transactionFee),
                  },
                  {
                    label: `${pricing.finance.instalments} monthly payments`,
                    value: formatCurrency(pricing.finance.monthlyPayment),
                  },
                  { label: "First payment", value: pricing.finance.firstPaymentTiming },
                  { label: "Total payable", value: formatCurrency(pricing.finance.totalPayable) },
                ]}
              />
            </div>
          )}
        </Card>
      </div>

      <Card className="mt-5">
        <CardHeader
          title={`${formatCurrency(annualCheck.insurerPremium)} live pricing check`}
          description="Customer-facing totals update from the selected insurer, premium, and calculator rules."
        />
        <div className="grid gap-3 md:grid-cols-3">
          <ResultCard
            icon={ReceiptText}
            label="Paid in full selling price"
            value={formatCurrency(annualCheck.totalSellingPrice)}
          />
          <ResultCard
            icon={CreditCard}
            label="10 monthly payments"
            value={formatCurrency(tenMonthCheck.finance?.monthlyPayment)}
            hint={tenMonthCheck.finance?.firstPaymentTiming}
          />
          <ResultCard
            icon={CalendarClock}
            label="11 monthly payments"
            value={formatCurrency(elevenMonthCheck.finance?.monthlyPayment)}
            hint={elevenMonthCheck.finance?.firstPaymentTiming}
          />
        </div>
      </Card>
    </>
  );
}

function CalculatorRows({
  items,
}: {
  items: Array<{ label: string; value: string; hide?: boolean }>;
}) {
  const visible = items.filter((item) => !item.hide);

  return (
    <dl className="divide-y divide-line/70">
      {visible.map((item) => (
        <div
          key={item.label}
          className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 py-2.5"
        >
          <dt className="min-w-0 text-xs font-medium uppercase tracking-[0.08em] text-content-subtle">
            {item.label}
          </dt>
          <dd className="shrink-0 whitespace-nowrap text-right text-sm font-medium tabular text-content">
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function HighlightTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/15 bg-white/10 p-3">
      <p className="text-xs uppercase tracking-[0.12em] text-white/55">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular">{value}</p>
    </div>
  );
}

function ResultCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-2xl border border-line bg-surface-sunken p-4">
      <div className="mb-4 grid size-10 place-items-center rounded-xl bg-gold-100 text-gold-800 dark:bg-gold-950/50 dark:text-gold-300">
        <Icon size={18} aria-hidden="true" />
      </div>
      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-content-subtle">
        {label}
      </p>
      <p className="mt-2 font-display text-2xl font-semibold tabular text-content">
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-content-subtle">{hint}</p>}
    </div>
  );
}
